/**
 * Transaction Service
 *
 * Service สำหรับจัดการ DepositWithdraw (ธุรกรรมฝาก-ถอน)
 */

import {
  createObject,
  updateObject,
  queryClass,
  callCloudFunction,
} from "@/services/adapters/parse/parse-client";
import { getAuditLogService } from "@/domain/audit-log";
import { createTransactionLog } from "@/domain/audit-log/audit-log.helper";
import type { Transaction } from "./transaction.types";

// ============================================
// Types
// ============================================

export interface CreateTransactionData {
  type: "Deposit" | "Withdraw";
  isBonus: boolean;
  memberId: string;
  webbankaccountId?: string | null;
  amount: number;
  status:
    | "Pending"
    | "Processing"
    | "Approved"
    | "Rejected"
    | "Completed"
    | "Cancelled";
  notes?: string;
  dateTime: string;
  image?: File | string | null;
  withdrawBy?: "Auto" | "Manual" | "Manaul"; // 'Manaul' = legacy typo
  promotionCode?: string;
}

export interface UpdateTransactionData {
  status?:
    | "Pending"
    | "Processing"
    | "Approved"
    | "Rejected"
    | "Completed"
    | "Cancelled";
  notes?: string;
  statusNote?: string;
  memberId?: string; // ✅ สำหรับแก้ไข member ของรายการที่ "ไม่สามารถระบุได้"
}

export interface TransactionQueryParams {
  page?: number;
  limit?: number;
  type?: "Deposit" | "Withdraw";
  status?:
    | "Pending"
    | "Processing"
    | "Approved"
    | "Rejected"
    | "Completed"
    | "Cancelled"
    | string[];
  startDate?: string;
  endDate?: string;
}

// ============================================
// Helper: Create Parse Pointer
// ============================================

function createPointer(className: string, objectId: string) {
  return {
    __type: "Pointer",
    className,
    objectId,
  };
}

// ============================================
// Mapping: Parse -> UI
// ============================================

function parseTransactionToUI(data: any): Transaction {
  // Type assertion to ensure Transaction type includes all fields
  // V2: Map credit_status (0-3) to string
  const mapSubStatus = (
    val: any
  ): "pending" | "processing" | "success" | "failed" | undefined => {
    if (val === undefined || val === null) return undefined;
    const map: Record<number, "pending" | "processing" | "success" | "failed"> =
      {
        0: "pending",
        1: "success",
        2: "processing",
        3: "failed",
      };
    return map[Number(val)] || "pending";
  };

  // ✅ Debug: Log status เพื่อตรวจสอบ
  const rawStatus = data.status;
  const mappedStatus = rawStatus || "Pending";
  if (!rawStatus || rawStatus !== "Pending") {
    console.log(
      `⚠️ [parseTransactionToUI] Status mapping: "${rawStatus}" → "${mappedStatus}" (ID: ${data.objectId})`
    );
  }

  return {
    id: data.objectId,
    type: data.type || "Deposit",
    isBonus: data.isBonus || false,
    memberId: data.member_id?.objectId || "",
    memberUsername: data.member_obj?.agent_username || "",
    memberName: `${data.member_obj?.firstname || ""} ${
      data.member_obj?.lastname || ""
    }`.trim(),
    memberBankAccount: data.member_obj?.bank_account || "",
    memberBankName: data.member_obj?.bank_name || "",
    // ✅ ดึง agent_status จาก member_obj ถ้าไม่มี member_agent_status (รองรับทั้งฝากและถอน)
    // member_agent_status: field ที่ set ใน DepositWithdraw (บางรายการอาจจะไม่มี)
    // member_obj.agent_status: ดึงจาก member object โดยตรง (มีทุกรายการ)
    memberAgentStatus:
      data.member_agent_status || data.member_obj?.agent_status,
    webBankId: data.webbankaccount_id?.objectId || "",
    webBankName: data.webbankaccount_obj?.bank_name || "",
    webBankAccount: data.webbankaccount_obj?.bank_account || "",
    webBankAccountName: data.webbankaccount_obj?.name || "",
    amount: parseFloat(data.amount) || 0,
    status: mappedStatus,
    dwStep: data.dw_step || undefined,
    notes: data.notes || "",
    statusNote: data.status_note,
    promotionCode: data.promotion_code,
    // ✅ Parse dateTime - รองรับทั้ง Date object (Parse Server) และ string
    dateTime: (() => {
      const dt = data.date_time;
      if (!dt) return "";
      if (typeof dt === "string") return dt;
      if (dt?.iso) return dt.iso; // Parse Server Date object
      if (dt instanceof Date) return dt.toISOString();
      return "";
    })(),
    // ✅ Parse datetimeCreated - รองรับทั้ง Date object (Parse Server) และ string
    datetimeCreated: (() => {
      const dt = data.datetime_created || data.createdAt;
      if (!dt) return "";
      if (typeof dt === "string") return dt;
      if (dt?.iso) return dt.iso; // Parse Server Date object
      if (dt instanceof Date) return dt.toISOString();
      return "";
    })(),
    image: data.image?._url || data.image?.url || null,
    // ✅ แก้ "Manaul" → "Manual" แต่ยังรองรับ "Manaul" จาก DB
    withdrawBy: data.withdrawBy === "Manaul" ? "Manual" : data.withdrawBy,
    isCreditWithdraw: data.is_credit_withdraw,
    isCashWithdraw: data.is_cash_withdraw,
    isCreditWithdrawAmount: data.is_credit_withdraw_amount,
    isCashWithdrawAmount: data.is_cash_withdraw_amount,
    transferId: data.transfer_id, // ✅ เพิ่ม transfer_id (ตัวเลขลำดับ)
    uuid: data.uuid,
    apiBankObj: data.api_bank_obj,
    promotionInfo: data.promotion_info, // ✅ เพิ่ม promotion_info

    // V2: 2-Layer Status
    creditStatus: mapSubStatus(data.is_credit_withdraw) || data.credit_status,
    transferStatus: mapSubStatus(data.is_cash_withdraw) || data.transfer_status,
    transferMethod: data.transfer_method,

    // V2: Error Info
    errorCode: data.error_code,
    errorMessage: data.error_message,

    // V2: Retry Info
    originalTransactionId: data.original_transaction_id,
    newTransactionId: data.new_transaction_id,
    isRetry: data.is_retry,
    retryCount: data.retry_count,

    // V2: Timestamps
    processedAt: data.processed_at,
    approvedAt: data.approved_at,
    completedAt: data.completed_at,
    rejectedAt: data.rejected_at,
    retriedAt: data.retried_at,
    creditRefundedAt: data.credit_refunded_at,
    creditDeductedAt: data.credit_deducted_at,
    transferCompletedAt: data.transfer_completed_at,

    // V2: User Actions
    approvedBy: data.approved_by?.objectId,
    rejectedBy: data.rejected_by?.objectId,
    retriedBy: data.retried_by?.objectId,

    // Transfer fields
    transferToBankName: data.transfer_obj?.to_bank_name,
    transferToBankAccount: data.transfer_obj?.to_account_number,
    transferToAccountName: data.transfer_obj?.to_account_name,
    transferFromBankName: data.transfer_obj?.from_bank_name,
    transferFromBankAccount: data.transfer_obj?.from_account_number,
    transferFromAccountName: data.transfer_obj?.from_account_name,
    transferNotes: data.transfer_obj?.notes,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    // ✅ ดึงชื่อผู้สร้างและผู้แก้ไข (firstname + lastname)
    // ถ้า include user object แล้ว จะได้ firstname/lastname จาก user object
    createdBy:
      data.user_created_id?.firstname && data.user_created_id?.lastname
        ? `${data.user_created_id.firstname} ${data.user_created_id.lastname}`.trim()
        : data.user_created_id?.firstname ||
          data.user_created_id?.username ||
          "",
    updatedBy:
      data.user_updated_id?.firstname && data.user_updated_id?.lastname
        ? `${data.user_updated_id.firstname} ${data.user_updated_id.lastname}`.trim()
        : data.user_updated_id?.firstname ||
          data.user_updated_id?.username ||
          "",
    createFrom: data.create_from,
    channel: data.chanel || data.channel || "",
  };
}

// ============================================
// Transaction Service
// ============================================

export const TransactionService = {
  /**
   * สร้างรายการธุรกรรมใหม่
   * ✅ V2: ตรวจสอบ useDepositV2 และใช้ createManualDepositV2 สำหรับ Deposit
   */
  async create(
    sessionToken: string,
    data: CreateTransactionData
  ): Promise<Transaction> {
    console.log("📝 [TransactionService] create:", data);

    // ✅ V2: ตรวจสอบ useDepositV2 สำหรับ Deposit
    if (data.type === "Deposit") {
      try {
        // ตรวจสอบ feature flag (ใช้ dynamic import เพื่อหลีกเลี่ยง circular dependency)
        const { getAppConfigService } = await import(
          "@/domain/app-config/app-config.service"
        );
        const appConfigService = getAppConfigService();
        const config = await appConfigService.getAppConfig();
        const useDepositV2 = config?.featureFlags?.useDepositV2 || false;

        console.log("🔍 [TransactionService] Checking Deposit V2:", {
          useDepositV2,
          configExists: !!config,
          featureFlags: config?.featureFlags,
        });

        if (useDepositV2) {
          console.log(
            "✅ [TransactionService] Using Deposit V2 (createManualDepositV2)"
          );
          console.log(
            "🔍 [TransactionService] SessionToken:",
            sessionToken ? `${sessionToken.substring(0, 20)}...` : "NULL"
          );

          // ✅ V2: เรียก createManualDepositV2 Cloud Function
          const result = await callCloudFunction(
            "createManualDepositV2",
            {
              memberId: data.memberId,
              amount: data.amount,
              webbankaccountId: data.webbankaccountId || null,
              notes: data.notes || "",
              dateTime: data.dateTime,
              isBonus: data.isBonus || false,
            },
            sessionToken // ✅ ส่ง sessionToken เพื่อให้ req.user ถูก set
          );

          console.log(
            "✅ [TransactionService] createManualDepositV2 result:",
            result
          );
          console.log(
            "✅ [TransactionService] Returned status:",
            result.status || "Pending"
          );

          // 📝 Audit Log - บันทึกการสร้างธุรกรรม V2
          try {
            const auditService = getAuditLogService();
            const auditInput = createTransactionLog(
              "deposit",
              result.transactionId,
              "Unknown Member", // Will be resolved by backend
              data.amount,
              true
            );
            await auditService.createAuditLog(sessionToken, {
              ...auditInput,
              action: "create",
              entityType: "DepositWithdraw",
              description: `สร้างรายการฝาก V2${
                data.isBonus ? " (โบนัส)" : ""
              }: ${data.amount.toLocaleString()} บาท`,
              metadata: {
                version: "V2",
                transactionId: result.transactionId,
              },
            });
            console.log("✅ [TransactionService] Audit log created (V2)");
          } catch (auditError) {
            console.warn(
              "⚠️ [TransactionService] Audit log failed (V2):",
              auditError
            );
          }

          // Return created object (need to fetch again for full data)
          return {
            id: result.transactionId,
            type: data.type,
            createFrom: "Panel" as const,
            channel: "",
            isBonus: data.isBonus,
            memberId: data.memberId,
            memberUsername: "",
            memberName: "",
            memberBankAccount: "",
            memberBankName: "",
            webBankId: data.webbankaccountId || "",
            webBankName: "",
            webBankAccount: "",
            amount: data.amount,
            status: result.status || "Pending", // ✅ V2: สร้างเป็น Pending เสมอ
            notes: data.notes || "",
            dateTime: data.dateTime,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }
      } catch (error) {
        console.warn(
          "⚠️ [TransactionService] Deposit V2 check failed, falling back to V1:",
          error
        );
        // Fall through to V1
      }
    }

    // ✅ V1: ใช้ createObject แบบเดิม (สำหรับ Withdraw หรือ Deposit V1)
    // Generate UUID (เหมือน parse-backend)
    const uuid = crypto.randomUUID().replace(/-/g, "");

    const parseData: Record<string, any> = {
      type: data.type,
      isBonus: data.isBonus,
      amount: data.amount,
      status: data.status,
      notes: data.notes || "",
      date_time: data.dateTime,
      uuid: uuid,
      chanel: "WebAdmin",
      create_from: "Panel",
      // ✅ V2: สร้างเป็น Pending (dw_step = 2) สำหรับ Withdraw
      dw_step: data.type === "Withdraw" ? 2 : 33,
      name: data.type,
      // Pointer to member
      member_id: createPointer("_User", data.memberId),
    };

    // Add bank account if not bonus
    if (!data.isBonus && data.webbankaccountId) {
      parseData.webbankaccount_id = createPointer(
        "WebBankAccount",
        data.webbankaccountId
      );
    }

    // ✅ V2: Withdraw specific - ไม่ส่ง withdrawBy ตอนสร้าง (จะเลือกตอนอนุมัติ)
    // if (data.type === "Withdraw") {
    //   parseData.withdrawBy = data.withdrawBy || "Auto";
    // }
    // V2: withdrawBy จะถูกตั้งค่าเมื่อ Admin อนุมัติ (Manual/Auto)

    // Promotion code
    if (data.promotionCode) {
      parseData.promotion_code = data.promotionCode;
    }

    // TODO: Handle image upload (Parse File)

    console.log("📡 [TransactionService] Sending to Parse (V1):", parseData);
    console.log("📡 [TransactionService] UUID:", parseData.uuid);

    const result = await createObject("DepositWithdraw", parseData);
    console.log("✅ [TransactionService] Created:", result);

    // 📝 Audit Log - บันทึกการสร้างธุรกรรม
    try {
      const auditService = getAuditLogService();
      const action = data.type === "Deposit" ? "deposit" : "withdraw";
      const auditInput = createTransactionLog(
        action,
        result.objectId,
        "Unknown Member", // Will be resolved by backend
        data.amount,
        true
      );
      await auditService.createAuditLog(sessionToken, {
        ...auditInput,
        action: "create",
        entityType: "DepositWithdraw",
        description: `สร้างรายการ${data.type === "Deposit" ? "ฝาก" : "ถอน"}${
          data.isBonus ? " (โบนัส)" : ""
        }: ${data.amount.toLocaleString()} บาท`,
      });
      console.log("✅ [TransactionService] Audit log created");
    } catch (auditError) {
      console.warn("⚠️ [TransactionService] Audit log failed:", auditError);
    }

    // Return created object (need to fetch again for full data)
    return {
      id: result.objectId,
      type: data.type,
      createFrom: "Panel" as const,
      channel: "",
      isBonus: data.isBonus,
      memberId: data.memberId,
      memberUsername: "",
      memberName: "",
      memberBankAccount: "",
      memberBankName: "",
      webBankId: data.webbankaccountId || "",
      webBankName: "",
      webBankAccount: "",
      amount: data.amount,
      status: data.status,
      notes: data.notes || "",
      dateTime: data.dateTime,
      createdAt: result.createdAt,
      updatedAt: result.createdAt,
    };
  },

  /**
   * อัปเดตรายการธุรกรรม
   */
  async update(
    sessionToken: string,
    id: string,
    data: UpdateTransactionData
  ): Promise<void> {
    console.log("📝 [TransactionService] update:", { id, data });

    const parseData: Record<string, any> = {};

    if (data.status !== undefined) parseData.status = data.status;
    if (data.notes !== undefined) parseData.notes = data.notes;
    if (data.statusNote !== undefined) parseData.status_note = data.statusNote;

    // ✅ รองรับการ update member_id (สำหรับรายการที่ "ไม่สามารถระบุได้")
    if (data.memberId !== undefined) {
      parseData.member_id = createPointer("_User", data.memberId);
      // ✅ หมายเหตุ: member_obj จะถูกอัพเดทอัตโนมัติโดย beforeSave hook
    }

    await updateObject("DepositWithdraw", id, parseData);
    console.log("✅ [TransactionService] Updated:", id);

    // 📝 Audit Log - บันทึกการอัปเดต
    try {
      const auditService = getAuditLogService();
      let action: "update" | "approve" | "reject" = "update";
      let description = `แก้ไขรายการธุรกรรม: ${id}`;

      if (data.status === "Approved") {
        action = "approve";
        description = `อนุมัติรายการธุรกรรม: ${id}${
          data.statusNote ? ` (${data.statusNote})` : ""
        }`;
      } else if (data.status === "Rejected") {
        action = "reject";
        description = `ปฏิเสธรายการธุรกรรม: ${id}${
          data.statusNote ? ` - ${data.statusNote}` : ""
        }`;
      }

      await auditService.createAuditLog(sessionToken, {
        action,
        entityType: "DepositWithdraw",
        entityId: id,
        entityName: `รายการธุรกรรม #${id.slice(-6)}`,
        description,
        severity: action === "reject" ? "warning" : "info",
        status: "success",
        metadata: data,
      });
      console.log("✅ [TransactionService] Audit log created");
    } catch (auditError) {
      console.warn("⚠️ [TransactionService] Audit log failed:", auditError);
    }
  },

  /**
   * ดึงรายการธุรกรรม
   */
  async findAll(
    _sessionToken: string,
    params: TransactionQueryParams = {}
  ): Promise<{ items: Transaction[]; total: number }> {
    console.log("🔍 [TransactionService] findAll:", params);

    const where: Record<string, any> = {
      deletedAt: { $exists: false },
    };

    if (params.type) {
      where.type = params.type;
    }

    if (params.status) {
      // V2: รองรับ array of statuses
      if (Array.isArray(params.status)) {
        where.status = { $in: params.status };
      } else {
        where.status = params.status;
      }
    }

    // Date range filter
    // ⚠️ ไม่ใช้ date filter ที่ backend สำหรับ Approved deposits
    // ✅ เพราะ datetime_created อาจจะต่างจาก date_time (วันที่ธุรกรรมจริง)
    // ✅ จะดึงทั้งหมดแล้ว filter ใน frontend ตาม date_time แทน
    // ✅ ตัวอย่าง: รายการวันที่ 5 ม.ค. อาจจะถูกสร้างวันที่ 4 ม.ค. (datetime_created)
    // if (params.startDate || params.endDate) {
    //   // ไม่ filter ที่ backend
    // }

    const result = await queryClass("DepositWithdraw", {
      where,
      limit: params.limit || 50,
      skip: params.page ? (params.page - 1) * (params.limit || 50) : 0,
      order: "-createdAt",
      include: ["user_created_id", "user_updated_id"],
    });

    console.log("🔍 [TransactionService] Result:", {
      total: result.count,
      itemsCount: result.results?.length,
      where: where, // ✅ Debug: แสดง where clause
      sampleDates: result.results?.slice(0, 5).map((r: any) => ({
        id: r.objectId,
        date_time: r.date_time,
        datetime_created: r.datetime_created,
        status: r.status,
        amount: r.amount,
      })),
    });

    // ✅ Debug: Log status ของรายการที่ query กลับมา
    if (result.results && result.results.length > 0) {
      const statuses = result.results.map((r: any) => ({
        id: r.objectId,
        status: r.status,
        type: r.type,
      }));
      console.log("🔍 [TransactionService] Query result statuses:", statuses);
    }

    return {
      items: (result.results || []).map(parseTransactionToUI),
      total: result.count || 0,
    };
  },

  /**
   * ดึงรายการธุรกรรมของ Member คนใดคนหนึ่ง
   * ไม่รวม deletedAt
   */
  async findByMemberId(
    _sessionToken: string,
    memberId: string,
    params: { limit?: number; page?: number } = {}
  ): Promise<{ items: Transaction[]; total: number }> {
    console.log("🔍 [TransactionService] findByMemberId:", memberId);

    const where: Record<string, any> = {
      deletedAt: { $exists: false },
      member_id: {
        __type: "Pointer",
        className: "_User",
        objectId: memberId,
      },
    };

    const result = await queryClass("DepositWithdraw", {
      where,
      limit: params.limit || 20,
      skip: params.page ? (params.page - 1) * (params.limit || 20) : 0,
      order: "-createdAt",
      include: ["user_created_id", "user_updated_id"],
    });

    console.log("🔍 [TransactionService] findByMemberId Result:", {
      memberId,
      total: result.count,
      itemsCount: result.results?.length,
    });

    return {
      items: (result.results || []).map(parseTransactionToUI),
      total: result.count || 0,
    };
  },

  /**
   * ดึงรายการธุรกรรมเดียว
   */
  async findOne(
    _sessionToken: string,
    id: string
  ): Promise<Transaction | null> {
    console.log("🔍 [TransactionService] findOne:", id);

    try {
      const result = await queryClass("DepositWithdraw", {
        where: { objectId: id },
        limit: 1,
        include: ["user_created_id", "user_updated_id"],
      });

      if (!result.results || result.results.length === 0) {
        return null;
      }

      return parseTransactionToUI(result.results[0]);
    } catch (error) {
      console.error("❌ [TransactionService] findOne error:", error);
      return null;
    }
  },

  /**
   * Soft Delete
   */
  async delete(sessionToken: string, id: string): Promise<void> {
    console.log("🗑️ [TransactionService] delete (soft):", id);

    await updateObject("DepositWithdraw", id, {
      deletedAt: { __type: "Date", iso: new Date().toISOString() },
    });

    console.log("✅ [TransactionService] Deleted:", id);

    // 📝 Audit Log - บันทึกการลบ
    try {
      const auditService = getAuditLogService();
      await auditService.createAuditLog(sessionToken, {
        action: "delete",
        entityType: "DepositWithdraw",
        entityId: id,
        entityName: `รายการธุรกรรม #${id.slice(-6)}`,
        description: `ลบรายการธุรกรรม: ${id}`,
        severity: "warning",
        status: "success",
      });
      console.log("✅ [TransactionService] Audit log created");
    } catch (auditError) {
      console.warn("⚠️ [TransactionService] Audit log failed:", auditError);
    }
  },

  /**
   * อนุมัติรายการ (Manual) - ใช้ Cloud Function
   */
  async approveManual(
    sessionToken: string,
    id: string,
    notes?: string
  ): Promise<void> {
    console.log("✅ [TransactionService] approveManual:", id, notes);

    try {
      await callCloudFunction(
        "approvedDepositWithdrawManaul",
        { id, notes },
        sessionToken
      );
      console.log("✅ [TransactionService] approveManual success");
    } catch (error) {
      console.error("❌ [TransactionService] approveManual error:", error);
      throw error;
    }
  },

  /**
   * อนุมัติรายการ (Auto) - ใช้ Cloud Function
   */
  async approveAuto(sessionToken: string, id: string): Promise<void> {
    console.log("✅ [TransactionService] approveAuto:", id);

    try {
      await callCloudFunction("approvedDepositWithdraw", { id }, sessionToken);
      console.log("✅ [TransactionService] approveAuto success");
    } catch (error) {
      console.error("❌ [TransactionService] approveAuto error:", error);
      throw error;
    }
  },

  /**
   * ปฏิเสธรายการ - ใช้ Cloud Function
   */
  async reject(
    sessionToken: string,
    id: string,
    reason?: string
  ): Promise<void> {
    console.log("❌ [TransactionService] reject:", id, reason);

    try {
      await callCloudFunction(
        "rejectedDepositWithdraw",
        { id, reason },
        sessionToken
      );
      console.log("✅ [TransactionService] reject success");
    } catch (error) {
      console.error("❌ [TransactionService] reject error:", error);
      throw error;
    }
  },

  /**
   * ส่งซ้ำ (Resend) - ใช้ Cloud Function
   */
  async resend(sessionToken: string, id: string): Promise<void> {
    console.log("🔄 [TransactionService] resend:", id);

    try {
      await callCloudFunction("resendDepositWithdraw", { id }, sessionToken);
      console.log("✅ [TransactionService] resend success");
    } catch (error) {
      console.error("❌ [TransactionService] resend error:", error);
      throw error;
    }
  },

  /**
   * ทำเสร็จ (Complete) - ใช้ Cloud Function
   */
  async complete(
    sessionToken: string,
    id: string,
    notes?: string
  ): Promise<void> {
    console.log("🏁 [TransactionService] complete:", id, notes);

    try {
      // ✅ ดึงข้อมูล transaction เพื่อใช้ใน audit log
      let transaction: Transaction | null = null;
      try {
        transaction = await this.findOne(sessionToken, id);
      } catch (e) {
        console.warn(
          "⚠️ [TransactionService] Could not fetch transaction for audit log:",
          e
        );
      }

      await callCloudFunction(
        "completeDepositWithdraw",
        { id, notes },
        sessionToken
      );
      console.log("✅ [TransactionService] complete success");

      // 📝 Audit Log - บันทึกการทำเสร็จ
      try {
        const auditService = getAuditLogService();
        await auditService.createAuditLog(sessionToken, {
          action: "complete",
          entityType: "DepositWithdraw",
          entityId: id,
          entityName: transaction
            ? `${transaction.type === "Deposit" ? "ฝาก" : "ถอน"} - ${
                transaction.memberName || "Unknown"
              }`
            : `รายการธุรกรรม #${id.slice(-6)}`,
          description: `ทำเสร็จ: ${
            transaction?.type === "Deposit" ? "ฝาก" : "ถอน"
          } จำนวน ${transaction?.amount?.toLocaleString() || "N/A"} บาท${
            notes ? ` (${notes})` : ""
          }`,
          severity: "info",
          status: "success",
          metadata: {
            type: transaction?.type || "unknown",
            amount: transaction?.amount || 0,
            notes: notes || null,
          },
        });
        console.log("✅ [TransactionService] Audit log created (complete)");
      } catch (auditError) {
        console.warn(
          "⚠️ [TransactionService] Audit log failed (complete):",
          auditError
        );
      }
    } catch (error) {
      console.error("❌ [TransactionService] complete error:", error);
      throw error;
    }
  },

  // ============================================
  // V2 Functions (Feature Flag Controlled)
  // ============================================

  /**
   * ตรวจสอบว่าใช้ V2 หรือไม่
   */
  async checkV2Status(sessionToken: string): Promise<{
    useV2: boolean;
    version: string;
    features: { fiveStatus: boolean };
  }> {
    console.log("🔍 [TransactionService] checkV2Status");

    try {
      const result = await callCloudFunction(
        "checkWithdrawV2Status",
        {},
        sessionToken
      );
      console.log("✅ [TransactionService] checkV2Status:", result);
      return result as {
        useV2: boolean;
        version: string;
        features: { fiveStatus: boolean };
      };
    } catch (error) {
      console.warn(
        "⚠️ [TransactionService] checkV2Status failed, using V1:",
        error
      );
      return { useV2: false, version: "1.0", features: { fiveStatus: false } };
    }
  },

  /**
   * V2: อนุมัติและตัดเครดิต (Auto)
   */
  async approveAutoV2(
    sessionToken: string,
    id: string,
    webbankaccountId?: string,
    reason?: string
  ): Promise<{ success: boolean; status: string }> {
    console.log("✅ [TransactionService] approveAutoV2:", {
      id,
      webbankaccountId,
      reason,
    });

    try {
      // ✅ ดึงข้อมูล transaction เพื่อใช้ใน audit log
      let transaction: Transaction | null = null;
      try {
        transaction = await this.findOne(sessionToken, id);
      } catch (e) {
        console.warn(
          "⚠️ [TransactionService] Could not fetch transaction for audit log:",
          e
        );
      }

      const result = await callCloudFunction(
        "approveWithdrawV2",
        {
          id,
          webbankaccountId: webbankaccountId || null,
          reason: reason || "",
        },
        sessionToken
      );
      console.log("✅ [TransactionService] approveAutoV2 success:", result);

      // 📝 Audit Log - บันทึกการอนุมัติ Auto
      try {
        const auditService = getAuditLogService();
        await auditService.createAuditLog(sessionToken, {
          action: "approve",
          entityType: "DepositWithdraw",
          entityId: id,
          entityName: transaction
            ? `${transaction.type === "Deposit" ? "ฝาก" : "ถอน"} - ${
                transaction.memberName || "Unknown"
              }`
            : `รายการธุรกรรม #${id.slice(-6)}`,
          description: `อนุมัติ Auto: ${
            transaction?.type === "Deposit" ? "ฝาก" : "ถอน"
          } จำนวน ${transaction?.amount?.toLocaleString() || "N/A"} บาท${
            reason ? ` (${reason})` : ""
          }`,
          severity: "info",
          status: "success",
          metadata: {
            type: transaction?.type || "unknown",
            amount: transaction?.amount || 0,
            method: "auto",
            webbankaccountId: webbankaccountId || null,
            reason: reason || null,
          },
        });
        console.log(
          "✅ [TransactionService] Audit log created (approveAutoV2)"
        );
      } catch (auditError) {
        console.warn(
          "⚠️ [TransactionService] Audit log failed (approveAutoV2):",
          auditError
        );
      }

      return result as { success: boolean; status: string };
    } catch (error) {
      console.error("❌ [TransactionService] approveAutoV2 error:", error);
      throw error;
    }
  },

  /**
   * V2: อนุมัติ Manual
   * - Deposit: verifyPayment → addCredit (2 steps)
   * - Withdraw: approveManual (1 step)
   */
  async approveManualV2(
    sessionToken: string,
    id: string,
    webbankaccountId?: string,
    reason?: string
  ): Promise<{ success: boolean; status: string }> {
    console.log("✅ [TransactionService] approveManualV2:", {
      id,
      webbankaccountId,
      reason,
    });

    try {
      // ✅ ดึงข้อมูล transaction เพื่อดู type
      const transaction = await this.findOne(sessionToken, id);
      if (!transaction) {
        throw new Error("Transaction not found");
      }

      if (transaction.type === "Deposit") {
        // ✅ Deposit V2: ทำ 2 ขั้นตอน
        console.log(
          "✅ [TransactionService] Deposit V2: verifyPayment → addCredit"
        );

        // Step 1: ยืนยันเงินเข้า (Pending → Processing)
        const verifyResult = await callCloudFunction(
          "verifyDepositPaymentV2",
          { id },
          sessionToken
        );
        console.log(
          "✅ [TransactionService] verifyPaymentV2 result:",
          verifyResult
        );

        // Step 2: เติมเครดิต (Processing → Completed)
        const creditResult = await callCloudFunction(
          "addDepositCreditV2",
          { id },
          sessionToken
        );
        console.log(
          "✅ [TransactionService] addCreditV2 result:",
          creditResult
        );

        // ✅ Note: Audit Log ถูกบันทึกโดย Backend Cloud Function แล้ว (verifyDepositPaymentV2, addDepositCreditV2)
        // Backend จะบันทึก audit log พร้อม fingerprint อัตโนมัติ

        return {
          success: true,
          status: creditResult.status || "Completed",
        };
      } else if (transaction.type === "Withdraw") {
        // ✅ Withdraw V2: อนุมัติ Manual (1 step)
        console.log("✅ [TransactionService] Withdraw V2: approveManual", {
          webbankaccountId,
          reason,
        });
        const result = await callCloudFunction(
          "approveWithdrawManualV2",
          {
            id,
            webbankaccountId: webbankaccountId || null,
            reason: reason || "",
          },
          sessionToken
        );
        console.log(
          "✅ [TransactionService] approveWithdrawManualV2 success:",
          result
        );

        // ✅ Note: Audit Log ถูกบันทึกโดย Backend Cloud Function แล้ว (approveWithdrawManualV2)
        // Backend จะบันทึก audit log พร้อม fingerprint อัตโนมัติ

        return result as { success: boolean; status: string };
      } else {
        throw new Error(`Unknown transaction type: ${transaction.type}`);
      }
    } catch (error) {
      console.error("❌ [TransactionService] approveManualV2 error:", error);
      throw error;
    }
  },

  /**
   * V2: ปฏิเสธ
   * - Deposit: rejectDepositV2
   * - Withdraw: rejectWithdrawV2
   */
  async rejectV2(
    sessionToken: string,
    id: string,
    reason?: string
  ): Promise<{ success: boolean; status: string; creditReturned?: boolean }> {
    console.log("❌ [TransactionService] rejectV2:", id, reason);

    try {
      // ✅ ดึงข้อมูล transaction เพื่อดู type
      const transaction = await this.findOne(sessionToken, id);
      if (!transaction) {
        throw new Error("Transaction not found");
      }

      if (transaction.type === "Deposit") {
        // ✅ Deposit V2: ปฏิเสธ
        console.log("❌ [TransactionService] Deposit V2: reject");
        const result = await callCloudFunction(
          "rejectDepositV2",
          { id, reason },
          sessionToken
        );
        console.log("✅ [TransactionService] rejectDepositV2 success:", result);

        // 📝 Audit Log - บันทึกการปฏิเสธ (Deposit)
        try {
          const auditService = getAuditLogService();
          await auditService.createAuditLog(sessionToken, {
            action: "reject",
            entityType: "DepositWithdraw",
            entityId: id,
            entityName: `ฝาก - ${transaction.memberName || "Unknown"}`,
            description: `ปฏิเสธ: ฝาก จำนวน ${
              transaction.amount?.toLocaleString() || "N/A"
            } บาท${reason ? ` - ${reason}` : ""}`,
            severity: "warning",
            status: "success",
            metadata: {
              type: "Deposit",
              amount: transaction.amount || 0,
              reason: reason || null,
            },
          });
          console.log(
            "✅ [TransactionService] Audit log created (rejectV2 - Deposit)"
          );
        } catch (auditError) {
          console.warn(
            "⚠️ [TransactionService] Audit log failed (rejectV2 - Deposit):",
            auditError
          );
        }

        return result as {
          success: boolean;
          status: string;
        };
      } else if (transaction.type === "Withdraw") {
        // ✅ Withdraw V2: ปฏิเสธ
        console.log("❌ [TransactionService] Withdraw V2: reject");
        const result = await callCloudFunction(
          "rejectWithdrawV2",
          { id, reason },
          sessionToken
        );
        console.log(
          "✅ [TransactionService] rejectWithdrawV2 success:",
          result
        );

        // 📝 Audit Log - บันทึกการปฏิเสธ (Withdraw)
        try {
          const auditService = getAuditLogService();
          await auditService.createAuditLog(sessionToken, {
            action: "reject",
            entityType: "DepositWithdraw",
            entityId: id,
            entityName: `ถอน - ${transaction.memberName || "Unknown"}`,
            description: `ปฏิเสธ: ถอน จำนวน ${
              transaction.amount?.toLocaleString() || "N/A"
            } บาท${reason ? ` - ${reason}` : ""}`,
            severity: "warning",
            status: "success",
            metadata: {
              type: "Withdraw",
              amount: transaction.amount || 0,
              reason: reason || null,
              creditReturned: result.creditReturned || false,
            },
          });
          console.log(
            "✅ [TransactionService] Audit log created (rejectV2 - Withdraw)"
          );
        } catch (auditError) {
          console.warn(
            "⚠️ [TransactionService] Audit log failed (rejectV2 - Withdraw):",
            auditError
          );
        }

        return result as {
          success: boolean;
          status: string;
          creditReturned: boolean;
        };
      } else {
        throw new Error(`Unknown transaction type: ${transaction.type}`);
      }
    } catch (error) {
      console.error("❌ [TransactionService] rejectV2 error:", error);
      throw error;
    }
  },

  /**
   * V2: Batch Reject (ยกเลิกหลายรายการพร้อมกัน)
   * ใช้สำหรับปุ่ม "ยกเลิกทั้งหมด" - เร็วกว่า loop 100 เท่า!
   */
  async batchRejectV2(
    sessionToken: string,
    transaction_ids: string[]
  ): Promise<{
    success: boolean;
    results: { success: number; failed: number; errors: any[] };
    message: string;
  }> {
    console.log(
      `🗑️ [TransactionService] batchRejectV2: ${transaction_ids.length} items`
    );

    try {
      const result = await callCloudFunction(
        "batchRejectTransactionsV2",
        { transaction_ids },
        sessionToken
      );
      console.log("✅ [TransactionService] batchRejectV2 success:", result);
      return result as {
        success: boolean;
        results: { success: number; failed: number; errors: any[] };
        message: string;
      };
    } catch (error) {
      console.error("❌ [TransactionService] batchRejectV2 error:", error);
      throw error;
    }
  },

  /**
   * V2: ทำซ้ำ (Retry)
   */
  async retryV2(
    sessionToken: string,
    id: string
  ): Promise<{ success: boolean; status: string }> {
    console.log("🔄 [TransactionService] retryV2:", id);

    try {
      const result = await callCloudFunction(
        "retryWithdrawV2",
        { id },
        sessionToken
      );
      console.log("✅ [TransactionService] retryV2 success:", result);
      return result as { success: boolean; status: string };
    } catch (error) {
      console.error("❌ [TransactionService] retryV2 error:", error);
      throw error;
    }
  },

  /**
   * V2: เสร็จสิ้น (Complete)
   */
  async completeV2(
    sessionToken: string,
    id: string,
    notes?: string
  ): Promise<{ success: boolean; status: string }> {
    console.log("🏁 [TransactionService] completeV2:", id, notes);

    try {
      // ✅ ดึงข้อมูล transaction เพื่อใช้ใน audit log
      let transaction: Transaction | null = null;
      try {
        transaction = await this.findOne(sessionToken, id);
      } catch (e) {
        console.warn(
          "⚠️ [TransactionService] Could not fetch transaction for audit log:",
          e
        );
      }

      const result = await callCloudFunction(
        "completeWithdrawV2",
        { id, notes },
        sessionToken
      );
      console.log("✅ [TransactionService] completeV2 success:", result);

      // 📝 Audit Log - บันทึกการทำเสร็จ
      try {
        const auditService = getAuditLogService();
        await auditService.createAuditLog(sessionToken, {
          action: "complete",
          entityType: "DepositWithdraw",
          entityId: id,
          entityName: transaction
            ? `${transaction.type === "Deposit" ? "ฝาก" : "ถอน"} - ${
                transaction.memberName || "Unknown"
              }`
            : `รายการธุรกรรม #${id.slice(-6)}`,
          description: `ทำเสร็จ: ${
            transaction?.type === "Deposit" ? "ฝาก" : "ถอน"
          } จำนวน ${transaction?.amount?.toLocaleString() || "N/A"} บาท${
            notes ? ` (${notes})` : ""
          }`,
          severity: "info",
          status: "success",
          metadata: {
            type: transaction?.type || "unknown",
            amount: transaction?.amount || 0,
            notes: notes || null,
          },
        });
        console.log("✅ [TransactionService] Audit log created (completeV2)");
      } catch (auditError) {
        console.warn(
          "⚠️ [TransactionService] Audit log failed (completeV2):",
          auditError
        );
      }

      return result as { success: boolean; status: string };
    } catch (error) {
      console.error("❌ [TransactionService] completeV2 error:", error);
      throw error;
    }
  },

  /**
   * V2: ยกเลิก (Cancel)
   * - Deposit: cancelDepositV2
   * - Withdraw: cancelWithdrawV2
   */
  async cancelV2(
    sessionToken: string,
    id: string,
    reason?: string
  ): Promise<{
    success: boolean;
    status: string;
    creditRefunded?: boolean;
    reason?: string;
  }> {
    console.log("🚫 [TransactionService] cancelV2:", id, reason);

    try {
      // ✅ ดึงข้อมูล transaction เพื่อดู type
      const transaction = await this.findOne(sessionToken, id);
      if (!transaction) {
        throw new Error("Transaction not found");
      }

      if (transaction.type === "Deposit") {
        // ✅ Deposit V2: ยกเลิก
        console.log("🚫 [TransactionService] Deposit V2: cancel");
        const result = await callCloudFunction(
          "cancelDepositV2",
          { id, reason },
          sessionToken
        );
        console.log("✅ [TransactionService] cancelDepositV2 success:", result);

        // 📝 Audit Log - บันทึกการยกเลิก (Deposit)
        try {
          const auditService = getAuditLogService();
          await auditService.createAuditLog(sessionToken, {
            action: "cancel",
            entityType: "DepositWithdraw",
            entityId: id,
            entityName: `ฝาก - ${transaction.memberName || "Unknown"}`,
            description: `ยกเลิก: ฝาก จำนวน ${
              transaction.amount?.toLocaleString() || "N/A"
            } บาท${reason ? ` - ${reason}` : ""}`,
            severity: "warning",
            status: "success",
            metadata: {
              type: "Deposit",
              amount: transaction.amount || 0,
              reason: reason || null,
              creditRefunded: result.creditRefunded || false,
            },
          });
          console.log(
            "✅ [TransactionService] Audit log created (cancelV2 - Deposit)"
          );
        } catch (auditError) {
          console.warn(
            "⚠️ [TransactionService] Audit log failed (cancelV2 - Deposit):",
            auditError
          );
        }

        return result as {
          success: boolean;
          status: string;
          creditRefunded?: boolean;
          reason?: string;
        };
      } else if (transaction.type === "Withdraw") {
        // ✅ Withdraw V2: ยกเลิก
        console.log("🚫 [TransactionService] Withdraw V2: cancel");
        const result = await callCloudFunction(
          "cancelWithdrawV2",
          { id, reason },
          sessionToken
        );
        console.log(
          "✅ [TransactionService] cancelWithdrawV2 success:",
          result
        );

        // 📝 Audit Log - บันทึกการยกเลิก (Withdraw)
        try {
          const auditService = getAuditLogService();
          await auditService.createAuditLog(sessionToken, {
            action: "cancel",
            entityType: "DepositWithdraw",
            entityId: id,
            entityName: `ถอน - ${transaction.memberName || "Unknown"}`,
            description: `ยกเลิก: ถอน จำนวน ${
              transaction.amount?.toLocaleString() || "N/A"
            } บาท${reason ? ` - ${reason}` : ""}`,
            severity: "warning",
            status: "success",
            metadata: {
              type: "Withdraw",
              amount: transaction.amount || 0,
              reason: reason || null,
              creditRefunded: result.creditRefunded || false,
            },
          });
          console.log(
            "✅ [TransactionService] Audit log created (cancelV2 - Withdraw)"
          );
        } catch (auditError) {
          console.warn(
            "⚠️ [TransactionService] Audit log failed (cancelV2 - Withdraw):",
            auditError
          );
        }

        return result as {
          success: boolean;
          status: string;
          creditRefunded?: boolean;
          reason?: string;
        };
      } else {
        throw new Error(`Unknown transaction type: ${transaction.type}`);
      }
    } catch (error) {
      console.error("❌ [TransactionService] cancelV2 error:", error);
      throw error;
    }
  },

  /**
   * ✅ ตรวจสอบรายการซ้ำก่อนสร้าง Manual Deposit
   * เช็คว่ามีรายการที่มี member, date, amount เหมือนกันหรือไม่
   */
  async checkDuplicate(
    sessionToken: string,
    params: {
      memberId: string;
      date: string; // YYYY-MM-DD
      amount: number;
      type: 'Deposit' | 'Withdraw';
    }
  ): Promise<{
    hasDuplicate: boolean;
    duplicates?: Array<{
      id: string;
      source: string; // 'Manual', 'Slip', 'LINE', etc.
      status: string;
      dateTime: string;
      amount: number;
    }>;
  }> {
    try {
      console.log('🔍 [TransactionService] checkDuplicate:', params);

      // เรียก Cloud Function เพื่อเช็ครายการซ้ำ
      const result = await callCloudFunction(
        'checkDuplicateTransaction',
        {
          member_id: params.memberId,
          date: params.date,
          amount: params.amount,
          type: params.type
        },
        sessionToken
      );

      console.log('✅ [TransactionService] checkDuplicate result:', result);

      return {
        hasDuplicate: result.has_duplicate || false,
        duplicates: result.duplicates || []
      };
    } catch (error) {
      console.error('❌ [TransactionService] checkDuplicate error:', error);
      // ถ้า error ให้คืนว่าไม่มีซ้ำ (ไม่บล็อกการทำงาน)
      return {
        hasDuplicate: false,
        duplicates: []
      };
    }
  },
};

export default TransactionService;
